import clsx from 'clsx';
import { useEffect, useRef } from 'react';
import './styles.css';
import { getTailwindColor } from '@/utils/colors';
import { parseTable, cleanHtml, color } from './table-utils';

export type TableProps = {
    innerHTML: string;
    attrs?: {
        className?: string;
        backgroundColor?: string;
        textColor?: string;
        secondaryBackgroundColor?: string;
        secondaryTextColor?: string;
        isFactsList?: boolean;
    };
};

type CSSVars = React.CSSProperties & Record<`--${string}`, string | undefined>;

export const Table = ({ innerHTML, attrs }: TableProps) => {
    const ref = useRef<HTMLDivElement & HTMLUListElement>(null);
    const isStriped = attrs?.className?.includes('is-style-stripes');

    const stripeVars = {
        '--stripe-bg': color(attrs?.backgroundColor),
        '--stripe-bg-secondary': color(attrs?.secondaryBackgroundColor),
        '--stripe-text-secondary': color(attrs?.secondaryTextColor),
    };

    useEffect(() => {
        if (!ref.current) {
            return;
        }

        ref.current.querySelectorAll<HTMLElement>(attrs?.isFactsList ? ':scope > li' : 'tr').forEach(row => {
            if (row.offsetHeight > 60) row.classList.add('tall-row');
        });

        if (!attrs?.isFactsList && isStriped) {
            const tbody = ref.current.querySelector('tbody');
            const tfoot = ref.current.querySelector('tfoot');
            if (tbody && tfoot) {
                const offset = tbody.querySelectorAll('tr').length;
                tfoot.querySelectorAll('tr').forEach((row, i) => {
                    row.classList.add((offset + i) % 2 === 0 ? 'stripe-odd' : 'stripe-even');
                });
            }
        }
    }, [innerHTML, attrs?.isFactsList, isStriped]);

    const textClass = attrs?.textColor && getTailwindColor(attrs.textColor, 'text');
    const bgClass = !isStriped && attrs?.backgroundColor && getTailwindColor(attrs.backgroundColor, 'bg');

    if (!attrs?.isFactsList) {
        return (
            <div
                ref={ref}
                className={clsx('table-class', textClass, bgClass)}
                dangerouslySetInnerHTML={{ __html: cleanHtml(innerHTML) }}
                style={isStriped ? (stripeVars as CSSVars) : undefined}
            />
        );
    }

    const { head, body, foot, hasFixedLayout } = parseTable(innerHTML);
    const stripeClass = (i: number) => (i % 2 === 0 ? 'fact-odd' : 'fact-even');
    const rows = [
        ...head.map((cells, i) => ({ key: `h${i}`, cells, className: 'fact-head' })),
        ...body.map((cells, i) => ({ key: `b${i}`, cells, className: stripeClass(i) })),
        ...foot.map((cells, i) => ({ key: `f${i}`, cells, className: clsx('fact-foot', stripeClass(body.length + i)) })),
    ];

    return (
        <ul
            ref={ref}
            className={clsx('facts-list', textClass, isStriped ? 'is-style-stripes' : bgClass, hasFixedLayout && 'has-fixed-layout')}
            style={{ '--text-color': color(attrs?.textColor), ...(isStriped && stripeVars) } as CSSVars}
        >
            {rows.map(({ key, cells, className }) => (
                <li key={key} className={className}>
                    <span className={clsx('fact-label', cells[0]?.className)}>
                        <span dangerouslySetInnerHTML={{ __html: cleanHtml(cells[0]?.content) }} />
                        <span className="r-sr-only">:</span>
                    </span>
                    <span className={clsx('fact-value', cells[1]?.className)} dangerouslySetInnerHTML={{ __html: cleanHtml(cells[1]?.content) }} />
                </li>
            ))}
        </ul>
    );
};
